import { Form as FinalForm, Field as FinalField, FormSpy } from 'react-final-form';
import createFocusOnErrorsDecorator from 'final-form-focus';
import { Typography } from '@mui/material';

// Automatically scroll/focus the first field with a validation error
const decorators = [createFocusOnErrorsDecorator()];

/**
 * Wrapper over React Final Form that injects useful defaults (focus-on-error)
 * and hides the boilerplate `<form>` element.
 */
export const Form = ({ children, render, ...rest }) => (
  <FinalForm decorators={decorators} {...rest}>
    {(formProps) => <form onSubmit={formProps.handleSubmit}>{(render || children)(formProps)}</form>}
  </FinalForm>
);

/**
 * Drop‑in replacement for the old `Field` from `react-final-form-html5-validation`.
 * Works with React 19 and does not rely on outdated dependencies.
 *
 * Pass any MUI component via the `component` prop (e.g. TextField).
 * Validation logic stays the same via the `validate` function.
 */
export const Field = ({
  component: Component,
  name,
  validate,
  valueOnChange,
  helperText,
  inputProps = {},
  ...rest
}) => (
  <FinalField name={name} validate={validate} {...rest}>
    {({ input, meta }) => {
      const showError =
        ((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched;

      const handleChange = (...args) => {
        if (valueOnChange) args = [valueOnChange(...args)];
        input.onChange(...args);
      };

      return (
        <Component
          {...rest}
          {...input}
          inputProps={{ ...inputProps, ...rest.inputProps }}
          value={input.value}
          onChange={handleChange}
          error={showError}
          helperText={showError ? meta.error || meta.submitError : helperText}
        />
      );
    }}
  </FinalField>
);

/**
 * Shows any submission‑level errors from the form.
 */
export const FormError = (props) => (
  <FormSpy subscription={{ error: true, submitError: true }}>
    {({ error, submitError }) =>
      error || submitError ? (
        <Typography component="p" paragraph color="error" {...props}>
          {error || submitError}
        </Typography>
      ) : null
    }
  </FormSpy>
);
